import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { isSet } from "../../../Utils/CommonFunctions";
import ScmInputTextField from "./ScmInputTextField";
import { extractStylesAsObjects, getOutputFieldsElement } from "../MarkupFunctions";

const ScmFormInputWrapper = ({ inputChild, formName, divIndex, formData, handleNestedInputChange, elmtParams, defProps }) => {
    const attributesOfChild = isSet(inputChild.attribs, {});
    const inputName = isSet(attributesOfChild?.name, '');
    const rawValueAttr = attributesOfChild?.value ?? attributesOfChild?.defaultvalue;
    const dynamicVar = elmtParams.dynamicVar;

    let resolvedFromAttr = null;
    if (dynamicVar && typeof rawValueAttr === 'string' && rawValueAttr.trim()) {
        let valuePath = rawValueAttr.trim();
        if (valuePath.startsWith('${') && valuePath.endsWith('}')) {
            valuePath = valuePath.slice(2, -1).trim();
        }
        const resolved = getOutputFieldsElement({ value: valuePath }, { dynamicVar, defProps });
        if (resolved !== null && resolved !== undefined && !React.isValidElement(resolved)) {
            resolvedFromAttr = resolved;
        }

        const rowKey = elmtParams.scmVarName || 'data';
        const row = dynamicVar[rowKey];
        if (
            (resolvedFromAttr === null || resolvedFromAttr === undefined || resolvedFromAttr === '') &&
            row &&
            typeof row === 'object' &&
            !Array.isArray(row)
        ) {
            const pathSuffix = valuePath.includes('.') ? valuePath.slice(valuePath.indexOf('.') + 1) : valuePath;
            if (
                inputName &&
                Object.prototype.hasOwnProperty.call(row, inputName) &&
                row[inputName] !== null &&
                row[inputName] !== undefined &&
                row[inputName] !== ''
            ) {
                resolvedFromAttr = row[inputName];
            } else if (
                pathSuffix &&
                pathSuffix !== inputName &&
                Object.prototype.hasOwnProperty.call(row, pathSuffix) &&
                row[pathSuffix] !== null &&
                row[pathSuffix] !== undefined &&
                row[pathSuffix] !== ''
            ) {
                resolvedFromAttr = row[pathSuffix];
            }
        }
    }

    const hasStored = formData[divIndex] && Object.prototype.hasOwnProperty.call(formData[divIndex], inputName);
    let currentValue = hasStored ? (formData[divIndex][inputName] ?? '') : (resolvedFromAttr ?? formData[divIndex]?.[inputName] ?? '');

    const seededRef = useRef(false);
    useEffect(() => {
        if (seededRef.current || !inputName || resolvedFromAttr == null || resolvedFromAttr === '' || hasStored) return;
        seededRef.current = true;
        handleNestedInputChange(inputName, resolvedFromAttr, divIndex);
    }, [inputName, divIndex, resolvedFromAttr, hasStored, handleNestedInputChange]);

    const handleInputChange = useCallback((objFromScmInputTextField, valFromScmInputTextField) => {
        handleNestedInputChange(inputName, valFromScmInputTextField, divIndex);
    }, [inputName, divIndex, handleNestedInputChange]);

    const nestedDefProps = useMemo(() => ({
        ...defProps,
        onChangeResourcesFieldHandler: handleInputChange,
        fieldFormPayload: {
            [inputName]: { selected_value: currentValue, api_name: inputName }
        }
    }), [defProps, handleInputChange, inputName]);

    const prevValueRef = useRef(currentValue);
    if (prevValueRef.current !== currentValue) {
        prevValueRef.current = currentValue;
        nestedDefProps.fieldFormPayload[inputName].selected_value = currentValue;
    }

    const elmtParamsForScmInput = useMemo(() => ({
        convertAndRenderElement: elmtParams.convertAndRenderElement,
        dynamicVar: elmtParams.dynamicVar,
        children: inputChild.children,
        formScope: formName,
        divName: `${formName}_div_${divIndex}`,
        defProps: nestedDefProps
    }), [elmtParams.convertAndRenderElement, elmtParams.dynamicVar, inputChild.children, formName, divIndex, nestedDefProps]);

    const styleString = isSet(attributesOfChild?.style, '');
    const styleObject = typeof styleString === 'string' ? extractStylesAsObjects(styleString) : styleString;

    return (
        <ScmInputTextField
            key={`${formName}_${inputName}_${divIndex}`}
            attributes={{ ...attributesOfChild, style: styleObject }}
            elmtParams={elmtParamsForScmInput}
        />
    );
};
export default ScmFormInputWrapper;
